import {comments} from "./comments";
import {document} from "./document";
import {elements} from "./elements";
import {nets} from "./nets";
import {pcbLayoutNodes} from "./pcbLayoutNodes";
import {pcbLayoutRules} from "./pcbLayoutRules";
import {properties} from "./properties";
import {selection} from "./selection";
import {subjects} from "./subjects";

export type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "./types";
export {
    createByUidSelector,
    createByUidsSelector,
    createCountSelector,
    createIsEmptySelector,
    createKeysSelector,
    createValuesSelector,
    defaultEquality,
    emptyArray,
    emptyObject,
} from "./utils";

// Re-export pure compute functions from derived selectors
export {computeDocumentConfigSectionWithOwnerDefault, computeElectricalDocumentSubset} from "./document/derived";
export {
    computeElementPropertyEvaluatedValue,
    computeElementPropertyValues,
    computeElementVisibilityToggleMap,
    computeMergedVisibilityToggleMap,
    computeSymbolDesignatorSuffixes,
} from "./elements/derived";

/**
 * Unified selector object with namespaced domains
 *
 * Usage:
 * - select.elements.all(state)
 * - select.elements.byUid('uid')(state)
 * - select.selection.elementUids(state)
 * - select.nets.byUid('uid')(state)
 * - select.properties.datasheet(state)
 * - select.document.uid(state)
 *
 * All selectors are typed and can be used with Zustand's useStore hook:
 * ```typescript
 * const elements = useDocumentStore(select.elements.all);
 * const element = useDocumentStore(select.elements.byUid(uid));
 * ```
 *
 * For memoized selectors (derived), use useShallow for object/array results:
 * ```typescript
 * const designators = useDocumentStore(useShallow(select.elements.designators));
 * ```
 */
export const select = {
    comments,
    elements,
    nets,
    pcbLayoutNodes,
    pcbLayoutRules,
    properties,
    selection,
    subjects,
    document,
} as const;

export type Select = typeof select;

export {comments, document, elements, nets, pcbLayoutNodes, pcbLayoutRules, properties, selection, subjects};
